import React, { useState, useEffect } from 'react';

const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
const REFRESH_OFFSET_MINUTES = 5; // Stagger by 5 minutes to allow Power BI to complete its refresh

// Calculate milliseconds until the next staggered refresh time (:05, :20, :35, :50)
const getMsUntilNextRefreshTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const milliseconds = now.getMilliseconds();
  
  // Target times are :05, :20, :35, :50 (quarter-hour + 5 minute offset)
  // Find which quarter we're in, then add offset
  const currentQuarter = Math.floor(minutes / 15) * 15;
  let nextRefreshMinute = currentQuarter + REFRESH_OFFSET_MINUTES;
  
  // If we've already passed this quarter's refresh time, move to next quarter
  if (minutes >= nextRefreshMinute || (minutes === nextRefreshMinute && (seconds > 0 || milliseconds > 0))) {
    nextRefreshMinute += 15;
  }
  
  const minutesUntilNext = nextRefreshMinute - minutes;
  
  // Calculate total ms until next refresh time
  const msUntilNext = (minutesUntilNext * 60 * 1000) - (seconds * 1000) - milliseconds;
  
  return msUntilNext;
};

const PowerBIReport = () => {
  // refreshKey is used to force iframe remount, triggering a reload
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [nextRefresh, setNextRefresh] = useState(null);

  useEffect(() => {
    let intervalId = null;

    const doRefresh = () => {
      setRefreshKey(prev => prev + 1);
      setLastRefresh(new Date());
      updateNextRefreshTime();
    };

    const updateNextRefreshTime = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      
      // Target times are :05, :20, :35, :50
      const currentQuarter = Math.floor(minutes / 15) * 15;
      let nextRefreshMinute = currentQuarter + REFRESH_OFFSET_MINUTES;
      
      // If we've already passed this quarter's refresh time, move to next quarter
      if (minutes >= nextRefreshMinute) {
        nextRefreshMinute += 15;
      }
      
      const next = new Date(now);
      next.setMinutes(nextRefreshMinute % 60);
      next.setSeconds(0);
      next.setMilliseconds(0);
      if (nextRefreshMinute >= 60) {
        next.setHours(next.getHours() + 1);
      }
      setNextRefresh(next);
    };

    // Set initial next refresh time
    updateNextRefreshTime();

    // Wait until the next staggered refresh time (:05, :20, :35, :50), then start the 15-minute interval
    const msUntilFirstRefresh = getMsUntilNextRefreshTime();
    
    const timeoutId = setTimeout(() => {
      doRefresh();
      // Now set up regular 15-minute interval aligned to quarter-hours
      intervalId = setInterval(doRefresh, REFRESH_INTERVAL_MS);
    }, msUntilFirstRefresh);

    // Cleanup: clear timeout and interval when component unmounts
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  return (
    <div>
      {/* Header */}
      <div style={{
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #dee2e6'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#333' }}>
          Matrix Platform Dashboard
        </h2>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
          This report displays live data from your Power BI workspace. 
          The report refreshes at :05, :20, :35, and :50 past each hour.
        </p>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#999' }}>
          Last refreshed: {lastRefresh.toLocaleTimeString()}
          {nextRefresh && ` • Next refresh: ${nextRefresh.toLocaleTimeString()}`}
        </p>
      </div>

      {/* Power BI Iframe */}
      <div style={{
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden',
        backgroundColor: '#fff'
      }}>
        <iframe 
          key={refreshKey}
          title="MatrixPlatform_PBI_Dashboard_Dev" 
          width="100%" 
          height="600" 
          src="https://app.powerbi.com/view?r=eyJrIjoiNWVhMmUxY2YtOTNlZS00NzkyLTk4NDMtM2I4MDhkNjQyZDY1IiwidCI6IjNjZGJhM2RmLWYzZTgtNDQ3OC04NzM2LWVlMmQ2MzAwNzI0NiJ9" 
          frameBorder="0" 
          allowFullScreen={true}
          style={{
            display: 'block',
            border: 'none'
          }}
        />
      </div>

      {/* Info Footer */}
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '4px',
        fontSize: '0.85rem',
        color: '#004085'
      }}>
        <strong>ℹ️ Note:</strong> This report uses DirectQuery and displays live data from your database. 
        Changes in the database are reflected immediately when you interact with filters or refresh your browser.
      </div>
    </div>
  );
};

export default PowerBIReport;



